// frontend/src/components/placements/PlacementAssessmentsPage.jsx
// This page provides access to pre-placement consumer assessments
// using the existing placement_assessments system

import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const PlacementAssessmentsPage = () => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const loadAssessments = async () => {
    if (!user?.tenant_id) return;

    setLoading(true);
    try {
      const res = await secureAxios.get(
        `${apiBase}/api/placement-assessments`,
        {
          params: { tenant_id: user.tenant_id },
        }
      );
      setAssessments(res.data);
    } catch (err) {
      console.error("Error loading assessments:", err);
      toast.error("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssessments();
  }, [user?.tenant_id]);

  const filteredAssessments = assessments.filter((a) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      a.consumer_name?.toLowerCase().includes(search) ||
      a.vocational_goal?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const getCompletionStatus = (assessment) => {
    // Check for key fields to determine completion
    const hasTransportation = assessment.transportation_available !== null;
    const hasWorkHistory = assessment.work_history !== null;
    const hasVocationalGoal = assessment.vocational_goal !== null;

    const completedFields = [
      hasTransportation,
      hasWorkHistory,
      hasVocationalGoal,
    ].filter(Boolean).length;

    if (completedFields === 3) {
      return { badge: "bg-success", label: "Complete", progress: 100 };
    }
    if (completedFields >= 2) {
      return { badge: "bg-info", label: "Mostly Complete", progress: 75 };
    }
    if (completedFields >= 1) {
      return {
        badge: "bg-warning text-dark",
        label: "In Progress",
        progress: 50,
      };
    }
    return { badge: "bg-secondary", label: "Started", progress: 25 };
  };

  return (
    <div className="container-fluid px-2 px-md-4 mt-3 mt-md-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h4 className="mb-0 h5">
          <i className="bi bi-clipboard-check me-2"></i>
          Assessments
        </h4>
      </div>

      {/* Info Card */}
      <div className="alert alert-info mb-2 mb-md-3 py-2 small">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Pre-Placement Assessments:</strong> Evaluate consumer readiness
        for job placement. Assessments document work history, transportation,
        education, preferences, and vocational goals to help match consumers
        with appropriate employment opportunities.
      </div>

      {/* Quick Stats */}
      {!loading && assessments.length > 0 && (
        <div className="row g-2 mb-2 mb-md-3">
          <div className="col-md-4">
            <div className="card bg-light">
              <div className="card-body text-center py-2">
                <h5 className="mb-0 h6">{assessments.length}</h5>
                <small className="text-muted">Total Assessments</small>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-success text-white">
              <div className="card-body text-center py-2">
                <h5 className="mb-0 h6">
                  {
                    assessments.filter(
                      (a) =>
                        a.transportation_available !== null &&
                        a.work_history !== null &&
                        a.vocational_goal !== null
                    ).length
                  }
                </h5>
                <small>Complete</small>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-warning">
              <div className="card-body text-center py-2">
                <h5 className="mb-0 h6">
                  {
                    assessments.filter(
                      (a) =>
                        a.transportation_available === null ||
                        a.work_history === null ||
                        a.vocational_goal === null
                    ).length
                  }
                </h5>
                <small>In Progress</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="row g-2 mb-2 mb-md-3">
        <div className="col-md-6">
          <div className="input-group input-group-sm">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search by consumer name or vocational goal..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
        <div className="col-12 col-md-6 text-end mt-2 mt-md-0">
          <Link to="/crm/job-placement" className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            Manage Assessments
          </Link>
        </div>
      </div>

      {/* Assessments Table */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : filteredAssessments.length === 0 ? (
        <div className="text-center py-5">
          <i
            className="bi bi-clipboard-check text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3 text-muted">No Assessments Found</h5>
          <p className="text-muted">
            {searchTerm
              ? "Try adjusting your search criteria"
              : "Create an assessment from the Job Placement section"}
          </p>
          <Link to="/crm/job-placement" className="btn btn-primary">
            <i className="bi bi-plus-circle me-2"></i>
            Go to Job Placement
          </Link>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm">
            <thead className="table-light">
              <tr>
                <th>Consumer</th>
                <th>Vocational Goal</th>
                <th>Transportation</th>
                <th>Education Level</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssessments.map((assessment) => {
                const status = getCompletionStatus(assessment);
                return (
                  <tr key={assessment.assessment_id}>
                    <td>
                      <strong>{assessment.consumer_name}</strong>
                    </td>
                    <td>
                      {assessment.vocational_goal ? (
                        <span
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "200px" }}
                          title={assessment.vocational_goal}
                        >
                          {assessment.vocational_goal}
                        </span>
                      ) : (
                        <span className="text-muted">Not specified</span>
                      )}
                    </td>
                    <td>
                      {assessment.transportation_available === true && (
                        <span className="badge bg-success">Available</span>
                      )}
                      {assessment.transportation_available === false && (
                        <span className="badge bg-danger">Not Available</span>
                      )}
                      {assessment.transportation_available === null && (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>{assessment.education_level || "-"}</td>
                    <td>
                      <span className={`badge ${status.badge}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>{formatDate(assessment.created_at)}</td>
                    <td className="text-end">
                      <div className="btn-group btn-group-sm">
                        <Link
                          to={`/crm/job-placement`}
                          className="btn btn-outline-primary"
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </Link>
                        <Link
                          to={`/crm/job-placement`}
                          className="btn btn-outline-info"
                          title="View Details"
                        >
                          <i className="bi bi-eye"></i>
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      {!loading && filteredAssessments.length > 0 && (
        <div className="text-muted small">
          Showing {filteredAssessments.length} assessment
          {filteredAssessments.length !== 1 ? "s" : ""}
        </div>
      )}

      {/* Assessment Tips */}
      <div className="card mt-3 mt-md-4">
        <div className="card-header bg-light py-2">
          <i className="bi bi-lightbulb me-2"></i>
          Assessment Best Practices
        </div>
        <div className="card-body p-2 p-md-3">
          <div className="row g-2 g-md-3">
            <div className="col-md-4">
              <h6>
                <i className="bi bi-1-circle me-2 text-primary"></i>
                Gather Background
              </h6>
              <p className="small text-muted">
                Document family situation, living arrangements, and support
                systems that may impact employment.
              </p>
            </div>
            <div className="col-md-4">
              <h6>
                <i className="bi bi-2-circle me-2 text-primary"></i>
                Assess Capabilities
              </h6>
              <p className="small text-muted">
                Review education, work history, skills, and any accommodations
                needed for successful placement.
              </p>
            </div>
            <div className="col-md-4">
              <h6>
                <i className="bi bi-3-circle me-2 text-primary"></i>
                Set Clear Goals
              </h6>
              <p className="small text-muted">
                Establish vocational goals that align with consumer interests,
                abilities, and market opportunities.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlacementAssessmentsPage;